/**
 * @file useElementFonts.ts
 * @description Данные подсказки о шрифте элемента (phase19.16, приёмка Р2а,
 * §7.17 п. 3; SB10, дополнение 2026-10-11; uiux-fonts-usage §4, §6):
 * оформление черновика (форма секции или вид сайта — один источник правды,
 * копий не держим), реестр шрифтов магазина и право на оформление.
 *
 * `null` — подсказки нет: у элемента не бывает текста или оформление ещё не
 * загружено (вне каркаса вида сайта — тоже).
 */

import { SITE_VIEW_PART_RIGHTS, resolveFontRole, type FontPart, type FontRole, type SiteFontRegistry, type SiteTheme } from "@chaiberi/shared";
import { useContext, useMemo } from "react";

import { useCurrentContext } from "../../auth/hooks";
import { useSectionForm } from "../../components/site-builder/section/section-form-context";
import { useShopFonts } from "../../components/site-view/shop-fonts-context";
import { SiteViewContext } from "../../components/site-view/site-view-context";
import { fontNoteLines, type FontNoteLine } from "../../lib/site-builder/element-font-lines";

export interface ElementFontsModel {
  lines: FontNoteLine[];
  theme: SiteTheme;
  registry: SiteFontRegistry;
  /** Может менять шрифты сайта (право на оформление — часть THEME). */
  canEdit: boolean;
  /** Список своих шрифтов не получился — кнопка «Открыть шрифты ›». */
  listFailed: boolean;
  /** Роль, которую раскрыть во вкладке «Шрифты» (первая часть; капс — по `eyebrowFrom`). */
  firstRole: FontRole;
}

export function useElementFonts(parts: readonly FontPart[]): ElementFontsModel | null {
  const formTheme = useSectionForm().theme;
  const view = useContext(SiteViewContext);
  const theme = formTheme ?? view?.parts?.theme.theme ?? null;
  const fonts = useShopFonts();
  // Мок контекста в тестах может прийти без роли — читаем осторожно.
  const permissions = useCurrentContext()?.role?.permissions ?? [];
  const canEdit = permissions.includes(SITE_VIEW_PART_RIGHTS.THEME.edit);
  const listFailed = fonts.error !== null;

  return useMemo(() => {
    const first = parts[0];
    if (!theme || !first) return null;
    const lines = fontNoteLines(parts, theme, { registry: fonts.registry, loaded: fonts.loaded, error: listFailed, requested: canEdit });
    return { lines, theme, registry: fonts.registry, canEdit, listFailed, firstRole: resolveFontRole(first.role, theme) };
  }, [parts, theme, fonts.registry, fonts.loaded, listFailed, canEdit]);
}
